<template>
  <div class="relative w-full py-8">
    <!-- Carousel Container -->
    <div class="relative flex items-center justify-center h-[550px] md:h-[700px]">
      
      <!-- Left Arrow -->
      <button
        @click="prevSlide"
        class="absolute left-2 md:left-8 z-40 flex items-center justify-center w-12 h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg transition-all duration-300"
        type="button"
      >
        <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <!-- Slides Container -->
      <div class="relative flex items-center justify-center w-full h-full">
        <div
          v-for="(image, index) in images"
          :key="index"
          class="absolute transition-all duration-500 ease-in-out cursor-pointer"
          :class="getSlideClass(index)"
          :style="getSlideStyle(index)"
          @click="handleSlideClick(index)"
        >
          <img
            :src="image"
            class="rounded-lg shadow-2xl object-contain max-h-full"
            :alt="'Locandina ' + (index + 1)"
          />
        </div>
      </div>

      <!-- Right Arrow -->
      <button
        @click="nextSlide"
        class="absolute right-2 md:right-8 z-40 flex items-center justify-center w-12 h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-lg transition-all duration-300"
        type="button"
      >
        <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Dots Indicator -->
    <div class="flex justify-center mt-6 space-x-2">
      <button
        v-for="(image, index) in images"
        :key="index"
        type="button"
        class="w-3 h-3 rounded-full transition-all duration-300"
        :class="activeIndex === index ? 'bg-teal-600 w-6' : 'bg-gray-300 hover:bg-gray-400'"
        @click="setActiveSlide(index)"
        :aria-label="'Vai alla locandina ' + (index + 1)"
      ></button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarouselComponent',
  data() {
    return {
      images: [],
      activeIndex: 0,
    };
  },
  methods: {
    async loadImages() {
      // Carica dinamicamente le immagini con pattern locandina1, locandina2, ecc.
      const loadedImages = [];
      let index = 1;
      const maxAttempts = 50; // Massimo numero di locandine
      
      while (index <= maxAttempts) {
        // Prova diversi formati di immagine
        const extensions = ['jpg', 'jpeg', 'png', 'webp'];
        let found = false;
        
        for (const ext of extensions) {
          const url = `${process.env.BASE_URL}locandine/locandina${index}.${ext}`;
          try {
            const response = await fetch(url, { method: 'HEAD' });
            if (response.ok) {
              loadedImages.push(url);
              found = true;
              break;
            }
          } catch {
            // Immagine non trovata, continua
          }
        }
        
        // Se non trova l'immagine con nessuna estensione, smetti di cercare
        if (!found) {
          break;
        }
        index++;
      }
      
      this.images = loadedImages;
    },
    getPosition(index) {
      const diff = index - this.activeIndex;
      const total = this.images.length;
      
      // Handle circular positioning
      if (diff === 0) return 'center';
      if (diff === 1 || diff === -(total - 1)) return 'right';
      if (diff === -1 || diff === (total - 1)) return 'left';
      if (diff === 2 || diff === -(total - 2)) return 'far-right';
      if (diff === -2 || diff === (total - 2)) return 'far-left';
      return 'hidden';
    },
    getSlideClass(index) {
      const position = this.getPosition(index);
      return {
        'z-30': position === 'center',
        'z-20': position === 'left' || position === 'right',
        'z-10': position === 'far-left' || position === 'far-right',
        'opacity-0 pointer-events-none': position === 'hidden',
      };
    },
    getSlideStyle(index) {
      const position = this.getPosition(index);
      
      switch (position) {
        case 'center':
          return {
            transform: 'translateX(0) scale(1)',
            opacity: 1,
            maxWidth: '420px',
            maxHeight: '600px',
          };
        case 'left':
          return {
            transform: 'translateX(-115%) scale(0.7)',
            opacity: 0.5,
            maxWidth: '320px',
            maxHeight: '450px',
          };
        case 'right':
          return {
            transform: 'translateX(115%) scale(0.7)',
            opacity: 0.5,
            maxWidth: '320px',
            maxHeight: '450px',
          };
        case 'far-left':
          return {
            transform: 'translateX(-200%) scale(0.5)',
            opacity: 0.25,
            maxWidth: '250px',
            maxHeight: '350px',
          };
        case 'far-right':
          return {
            transform: 'translateX(200%) scale(0.5)',
            opacity: 0.25,
            maxWidth: '250px',
            maxHeight: '350px',
          };
        default:
          return {
            transform: 'translateX(0) scale(0.5)',
            opacity: 0,
            maxWidth: '250px',
            maxHeight: '350px',
          };
      }
    },
    handleSlideClick(index) {
      const position = this.getPosition(index);
      if (position === 'left' || position === 'far-left') {
        this.prevSlide();
      } else if (position === 'right' || position === 'far-right') {
        this.nextSlide();
      }
    },
    setActiveSlide(index) {
      this.activeIndex = index;
    },
    nextSlide() {
      this.activeIndex = (this.activeIndex + 1) % this.images.length;
    },
    prevSlide() {
      this.activeIndex = (this.activeIndex - 1 + this.images.length) % this.images.length;
    },
  },
  mounted() {
    this.loadImages();
  },
};
</script>

<style scoped>
/* Smooth transitions for all slides */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
  